import { useState, Suspense, useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment } from '@react-three/drei';
import * as THREE from 'three';

const modelFiles = [
  { path: '/GLB/batman_rigged_hatchxr.glb', name: 'Batman' },
  { path: '/GLB/chibi_zenitsu.glb', name: 'Chibi Zenitsu' },
  { path: '/GLB/halo-infinite-master-chief-rigged-walk..glb', name: 'Master Chief' },
  { path: '/GLB/lady_deadpool_free_3d_by_oscar_creativo.glb', name: 'Lady Deadpool' },
  { path: '/GLB/one_piece_bounty_rush_luffy__gear_4_snakeman.glb', name: 'Luffy Snakeman' },
  { path: '/GLB/pale_knight_animated.glb', name: 'Pale Knight' },
  { path: '/GLB/rigged_optimus_primal__tf_rotb.glb', name: 'Optimus Primal' },
  { path: '/GLB/rigged_t-rex_fabulous.glb', name: 'T-Rex' },
  { path: '/GLB/scourger_rig.glb', name: 'Scourger' },
  { path: '/GLB/selian_siren_rig.glb', name: 'Selian Siren' },
  { path: '/GLB/rigged_anime_male_character-1.glb', name: 'Anime Character' },
  { path: '/GLB/spiderman_rigged.glb', name: 'Spider-Man' },
];

// Preload all models
modelFiles.forEach(model => {
  useGLTF.preload(model.path);
});

function Model({ path, onLoaded }) {
  const { scene } = useGLTF(path);
  const groupRef = useRef();
  const normalizedRef = useRef(false);

  useEffect(() => {
    if (scene && !normalizedRef.current) {
      // Calculate bounding box
      const box = new THREE.Box3().setFromObject(scene);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      
      // Get max dimension and normalize to size 3
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 3 / maxDim;
      
      scene.scale.multiplyScalar(scale);
      
      // Recenter after scaling
      box.setFromObject(scene);
      box.getCenter(center);
      scene.position.sub(center);
      
      normalizedRef.current = true;
      if (onLoaded) onLoaded();
    }
  }, [scene, onLoaded]);

  // Auto-rotate
  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.3;
    }
  });

  return (
    <group ref={groupRef}>
      <primitive object={scene} />
    </group>
  );
}

function LoadingBox() {
  const meshRef = useRef();
  
  useFrame((_, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta;
      meshRef.current.rotation.y += delta;
    }
  });

  return (
    <mesh ref={meshRef}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#f59e0b" wireframe />
    </mesh>
  );
}

const ThreeDSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleGenerate = () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    setIsLoading(true);
    
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % modelFiles.length);
      setIsGenerating(false);
    }, 1500);
  };

  const handleModelLoaded = () => {
    setIsLoading(false);
  };

  const current = modelFiles[currentIndex];

  return (
    <div id="3d-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
        <div>
          <div className="glass-panel rounded-xl sm:rounded-[2rem] p-4 sm:p-6 relative overflow-hidden">
            {(isGenerating || isLoading) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-20 rounded-xl sm:rounded-[2rem]">
                <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-3 sm:mb-4"></div>
                <p className="text-white/70 text-xs sm:text-sm">{isGenerating ? 'Building 3D model...' : 'Loading model...'}</p>
              </div>
            )}
            <div className="aspect-square rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-b from-gray-900 to-black relative">
              <Canvas camera={{ position: [0, 1, 6], fov: 45 }} key={currentIndex}>
                <ambientLight intensity={1.2} />
                <directionalLight position={[10, 10, 5]} intensity={2} />
                <directionalLight position={[-10, 5, -5]} intensity={1} />
                <spotLight position={[0, 10, 0]} intensity={1} />
                <Suspense fallback={<LoadingBox />}>
                  <Model 
                    path={current.path} 
                    onLoaded={handleModelLoaded}
                  />
                  <Environment preset="studio" />
                </Suspense>
                <OrbitControls 
                  enableZoom={false}
                  enablePan={false}
                  makeDefault
                />
              </Canvas>
            </div>
            <div className="mt-3 sm:mt-4 flex items-center justify-between">
              <span className="text-[10px] sm:text-xs text-white/40 font-mono">{current.name}</span>
              <span className="text-[10px] sm:text-xs text-white/40">Drag to rotate</span>
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-primary/20">
              <span className="material-symbols-outlined text-primary text-xl sm:text-2xl">view_in_ar</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white">AI 3D Model Generation</h3>
          </div>
          <p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-lg">
            Generate production-ready 3D models from text or images. Export to any format for games, films, or AR/VR applications.
          </p>
          <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
            <li className="flex items-center gap-2 sm:gap-3 text-white/60 text-sm">
              <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
              Text or image to 3D mesh conversion
            </li>
            <li className="flex items-center gap-2 sm:gap-3 text-white/60 text-sm">
              <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
              Auto-generated PBR textures and materials
            </li>
            <li className="flex items-center gap-2 sm:gap-3 text-white/60 text-sm">
              <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
              Export to OBJ, FBX, GLTF, USD formats
            </li>
          </ul>
          <button 
            onClick={handleGenerate}
            disabled={isGenerating}
            className="btn-glow flex h-10 sm:h-12 items-center justify-center rounded-full bg-primary px-6 sm:px-8 text-xs sm:text-sm font-bold text-background-dark disabled:opacity-50"
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThreeDSection;
